import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Tooltip
} from '@mui/material';
import { get } from '../../services/apiService';

/**
 * ProductionBugsByFixVersionChart component
 * Displays a hotspot/heatmap visualization of production defects grouped by fix version
 * Color intensity represents the number of defects
 * @returns {JSX.Element} ProductionBugsByFixVersionChart component
 */
const ProductionBugsByFixVersionChart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await get('/api/workitems/production-bugs/public/by-fix-version');
        
        if (response.success) {
          setData(response.data || []);
        } else {
          throw new Error(response.message || 'Failed to fetch data');
        }
      } catch (err) {
        console.error('Error fetching production bugs by fix version:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          Loading chart data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Alert severity="info">
        No data available for production defects by fix version.
      </Alert>
    );
  }

  // Function to extract semantic version string (for display)
  const extractSemanticVersion = (version) => {
    if (!version || version === 'Not Specified') {
      return version || 'Not Specified';
    }
    
    const versionString = version.toString();
    let versionsToCheck = [versionString];
    
    if (versionString.includes(';')) {
      // Split by semicolon and trim each part
      versionsToCheck = versionString.split(';').map(v => v.trim()).filter(v => v);
    }
    
    // Look for semantic version (x.y.z format) in the versions
    for (const v of versionsToCheck) {
      // Try to extract full semantic version (x.y.z)
      const match = v.match(/(\d+)\.(\d+)\.(\d+)/);
      if (match) {
        return match[0]; // Return the matched version string
      }
    }
    
    // If no x.y.z found, try x.y format
    for (const v of versionsToCheck) {
      const match2 = v.match(/(\d+)\.(\d+)/);
      if (match2) {
        return match2[0]; // Return the matched version string
      }
    }
    
    // If no x.y found, try single number
    for (const v of versionsToCheck) {
      const match3 = v.match(/(\d+)/);
      if (match3) {
        return match3[0]; // Return the matched version string
      }
    }
    
    // If no version pattern found, return original
    return versionString;
  };

  // Function to parse semantic version (x.y.z format) for sorting
  const parseVersion = (version) => {
    if (!version || version === 'Not Specified') {
      return { major: 0, minor: 0, patch: 0, original: version };
    }
    
    // If multiple versions separated by semicolon, find the semantic version
    const versionString = version.toString();
    let versionsToCheck = [versionString];
    
    if (versionString.includes(';')) {
      // Split by semicolon and trim each part
      versionsToCheck = versionString.split(';').map(v => v.trim()).filter(v => v);
    }
    
    // Look for semantic version (x.y.z format) in the versions
    for (const v of versionsToCheck) {
      // Try to extract full semantic version (x.y.z)
      const match = v.match(/(\d+)\.(\d+)\.(\d+)/);
      if (match) {
        return {
          major: parseInt(match[1], 10),
          minor: parseInt(match[2], 10),
          patch: parseInt(match[3], 10),
          original: version
        };
      }
    }
    
    // If no x.y.z found, try x.y format
    for (const v of versionsToCheck) {
      const match2 = v.match(/(\d+)\.(\d+)/);
      if (match2) {
        return {
          major: parseInt(match2[1], 10),
          minor: parseInt(match2[2], 10),
          patch: 0,
          original: version
        };
      }
    }
    
    // If no x.y found, try single number
    for (const v of versionsToCheck) {
      const match3 = v.match(/(\d+)/);
      if (match3) {
        return {
          major: parseInt(match3[1], 10),
          minor: 0,
          patch: 0,
          original: version
        };
      }
    }
    
    // If no version pattern found, put it at the end
    return { major: 999999, minor: 999999, patch: 999999, original: version };
  };

  // Sort data by version number (semantic versioning)
  const sortedData = [...data].sort((a, b) => {
    const versionA = parseVersion(a.fixVersion);
    const versionB = parseVersion(b.fixVersion);
    
    // Compare major version
    if (versionA.major !== versionB.major) {
      return versionB.major - versionA.major; // Descending order (newest first)
    }
    
    // Compare minor version
    if (versionA.minor !== versionB.minor) {
      return versionB.minor - versionA.minor; // Descending order
    }
    
    // Compare patch version
    if (versionA.patch !== versionB.patch) {
      return versionB.patch - versionA.patch; // Descending order
    }
    
    // If versions are equal, maintain original order
    return 0;
  });

  // Prepare data and calculate color gradient
  const topData = sortedData.slice(0, 30); // Show more items in hotspot view
  const maxCount = Math.max(...topData.map(item => item.count), 1);
  const minCount = Math.min(...topData.map(item => item.count), 0);

  // Function to calculate color intensity based on count
  const getColorIntensity = (count) => {
    if (maxCount === minCount) return 0.1; // All same value
    const normalized = (count - minCount) / (maxCount - minCount);
    return 0.1 + (normalized * 0.9); // Range from 0.1 to 1.0
  };

  // Function to get color based on intensity
  const getColor = (intensity) => {
    // Using a gradient from light blue to dark blue
    const r = Math.round(33 + (intensity - 0.1) * (13 - 33) / 0.9);
    const g = Math.round(150 + (intensity - 0.1) * (68 - 150) / 0.9);
    const b = Math.round(243 + (intensity - 0.1) * (52 - 243) / 0.9);
    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Production Defects by Fix Version
      </Typography>
      <Box sx={{ mt: 2, mb: 2 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 1,
            '& > *': {
              flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 calc(33.333% - 8px)', md: '1 1 calc(20% - 8px)' },
              minWidth: { xs: 'calc(50% - 8px)', sm: 'calc(33.333% - 8px)', md: 'calc(20% - 8px)' },
              maxWidth: { xs: 'calc(50% - 8px)', sm: 'calc(33.333% - 8px)', md: 'calc(20% - 8px)' }
            }
          }}
        >
          {topData.map((item, index) => {
            const originalFixVersion = item.fixVersion || 'Not Specified';
            const fixVersion = extractSemanticVersion(originalFixVersion); // Extract only semantic version for display
            const count = item.count;
            const intensity = getColorIntensity(count);
            const color = getColor(intensity);
            
            return (
              <Box key={index}>
                <Tooltip 
                  title={`${originalFixVersion}: ${count} defect${count !== 1 ? 's' : ''}`}
                  arrow
                >
                  <Box
                    sx={{
                      backgroundColor: color,
                      color: intensity > 0.5 ? 'white' : 'rgba(0, 0, 0, 0.87)',
                      p: 1.5,
                      borderRadius: 1,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: 2
                      },
                      minHeight: 60,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {fixVersion}
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: '1.25rem'
                      }}
                    >
                      {count}
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      </Box>
      {data.length > 30 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Showing top 30 fix versions (out of {data.length} total)
        </Typography>
      )}
    </Paper>
  );
};

export default ProductionBugsByFixVersionChart;

